import { z } from "zod";

/**
 * Full payload for the lead "color dot" relationship hover (PREC-3547). One endpoint returns
 * everything the consolidated popover renders, fetched on demand when a dot is hovered:
 *
 * - `lead`          — identity header (name, title, company, avatar)
 * - `activeCampaign`— the campaign the lead is currently being contacted in (red dot)
 * - `pastOutreach`  — campaigns the lead was contacted in before, with first/last reply
 *                     sentiment (yellow dot)
 * - `connections`   — the user's LinkedIn connections that match this lead (blue dot)
 * - `otherLists`    — other lists the lead's prospect appears on (grey dot)
 *
 * Each section maps to one dot color; the FE only renders the sections whose dot is present in
 * `row.labels`. All dates are ISO strings (Date values serialize to ISO over the wire).
 */

/** Reply sentiment classes — mirror the API `ProspectInterest` enum (see apps/web prospect.enum). */
export const LEAD_REPLY_SENTIMENTS = [
	"INTERESTED",
	"BAD_TIMING",
	"WRONG_PERSON",
	"NOT_INTERESTED",
	"NEUTRAL",
] as const;

export type LeadReplySentiment = (typeof LEAD_REPLY_SENTIMENTS)[number];

export const leadReplySentimentSchema = z.enum(LEAD_REPLY_SENTIMENTS);

export const leadRelationshipLeadSchema = z.object({
	name: z.string(),
	title: z.string().nullable(),
	company: z.string().nullable(),
	avatarUrl: z.string().nullable(),
});

export const leadRelationshipActiveCampaignSchema = z.object({
	campaignName: z.string(),
	assignedAt: z.string().nullable(),
	/** e.g. "Step 2 of 5" — best-effort, null when it can't be resolved. */
	stepLabel: z.string().nullable(),
	/**
	 * The prospect's `list_person` row that actually carries the running campaign. It is NOT
	 * necessarily the row the caller asked about: the red `IN_ACTIVE_CAMPAIGN` dot is computed
	 * prospect-wide (any list), so a lead viewed on list A can be campaigning on list B. Consumers
	 * that act on the campaign (pause) or link to it must use these fields, not the row they hold
	 * (PREC-3906).
	 */
	listPersonId: z.number(),
	listId: z.number(),
	listName: z.string(),
	/** Raw AssignStatus of that row — the FE resolves the Inbox/Outbox deep link from it. */
	assignStatus: z.string(),
});

export const leadRelationshipPastOutreachSchema = z.object({
	campaignName: z.string(),
	assignedAt: z.string().nullable(),
	firstSentiment: leadReplySentimentSchema.nullable(),
	lastSentiment: leadReplySentimentSchema.nullable(),
	/** The outreach in this campaign has finished (assignStatus ENDED/DONE). */
	ended: z.boolean(),
});

export const leadRelationshipConnectionSchema = z.object({
	name: z.string(),
	connectedAt: z.string().nullable(),
});

export const leadRelationshipOtherListSchema = z.object({
	/**
	 * The prospect's `list_person` row on that list — the identity a bulk action needs to
	 * act on this membership (the "Add to CRM" list picker sends it as `listPersonIds`).
	 * The list id alone is not enough: a selection is resolved per list person.
	 */
	listPersonId: z.number(),
	listId: z.number(),
	listName: z.string(),
	/** Raw AssignStatus value — the FE translates it (inbox.assignStatus.*) into a chip. */
	assignStatus: z.string(),
	addedAt: z.string().nullable(),
});

export const leadRelationshipSchema = z.object({
	lead: leadRelationshipLeadSchema,
	activeCampaign: leadRelationshipActiveCampaignSchema.nullable(),
	pastOutreach: z.array(leadRelationshipPastOutreachSchema),
	connections: z.array(leadRelationshipConnectionSchema),
	otherLists: z.array(leadRelationshipOtherListSchema),
});

export const leadRelationshipResponseSchema = z.object({
	data: leadRelationshipSchema,
});

export type LeadRelationshipLead = z.infer<typeof leadRelationshipLeadSchema>;
export type LeadRelationshipActiveCampaign = z.infer<typeof leadRelationshipActiveCampaignSchema>;
export type LeadRelationshipPastOutreach = z.infer<typeof leadRelationshipPastOutreachSchema>;
export type LeadRelationshipConnection = z.infer<typeof leadRelationshipConnectionSchema>;
export type LeadRelationshipOtherList = z.infer<typeof leadRelationshipOtherListSchema>;
export type LeadRelationship = z.infer<typeof leadRelationshipSchema>;
export type LeadRelationshipResponse = z.infer<typeof leadRelationshipResponseSchema>;
